import React, { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { C, TABLE, DAY_THEMES, DAY_NAMES } from "../constants";
import { LAYOUT } from "../constants/colors";
import { parseDayKey } from "../utils";
import {
  regenerateDay,
  refinePlan,
  getHomeworkPlan,
  syncHomeworkPlan,
  toggleHomeworkTask,
  toggleHomeworkNight,
  resetHomeworkProgress,
  getAuthToken,
} from "../api";
import { RichLineText } from "./richPlanText";
import EmptyState from "./EmptyState";
import TableColumnsMenu from "./TableColumnsMenu";
import { useTablePersonalize } from "../hooks/useTablePersonalize";
import {
  SLOT_OVERRIDE_KEY,
  splitStoredOverrides,
  homeworkTaskFamily,
} from "../constants/dayActivities";
import { homeworkHasBody, homeworkRowsOnly, isHomeworkActivity, normalizePlanRows } from "../utils/homeworkRows";

const HW_COLS = [
  { id: "check", label: "Done", icon: "ti-square-check", width: "6%" },
  { id: "type", label: "Type", icon: "ti-tag", width: "18%" },
  { id: "content", label: "Task / Content", icon: "ti-align-left", width: "76%" },
];

function cellText(val) {
  return String(val ?? "").replace(/\\n/g, "\n");
}

function sanitizeFilePart(s) {
  return String(s || "homework")
    .trim()
    .replace(/[^\w\-]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "")
    .slice(0, 48) || "homework";
}

export function extractHomeworkEntries(planRows = []) {
  const byDay = {};
  normalizePlanRows(planRows).forEach((row) => {
    const dayId = row[0];
    if (!byDay[dayId]) byDay[dayId] = [];
    byDay[dayId].push(row);
  });

  const entries = [];
  Object.keys(byDay).forEach((dayId) => {
    byDay[dayId].forEach((row, index) => {
      if (isHomeworkActivity(row[2]) && String(row[3] || "").replace(/\\n/g, "\n").trim()) {
        entries.push({ row, index, dayId });
      }
    });
  });
  return entries;
}

function weekNumbersFromEntries(entries) {
  const set = new Set();
  entries.forEach(({ row }) => {
    const p = parseDayKey(row[0]);
    if (p) set.add(p.week || 1);
  });
  return [...set].sort((a, b) => a - b);
}

const TASK_TITLE_RE =
  /^(leetcode(?:\s+practice)?|exercism(?:\s+practice)?|linkedin(?:\s+article)?|agent workbench|case study(?:\s+review)?)\b/i;

/** Match DailyPlanTable row coloring */
function getTaskStyle(kind = "") {
  const t = String(kind || "").toLowerCase();
  if (t.includes("leetcode") || t.includes("exercism") || t.includes("coding")) {
    return { bg: "#E7F0F6", accent: "#1B4F72", label: t.includes("exercism") ? "Exercism" : "LeetCode" };
  }
  if (t.includes("agent")) return { bg: "#EEF2FF", accent: "#4338CA", label: "Agent Workbench" };
  if (t.includes("linkedin")) return { bg: "#EFF6FF", accent: "#2563EB", label: "LinkedIn" };
  if (t.includes("case")) return { bg: "#E7F0F6", accent: "#1B4F72", label: "Case study" };
  return { bg: "#FFFFFF", accent: "#374151", label: "Task" };
}

function parseField(lines, key) {
  const re = new RegExp(`^${key}\\s*:\\s*(.*)$`, "i");
  const hit = lines.find((l) => re.test(l));
  return hit ? hit.replace(re, "$1").trim() : "";
}

function pickHomeworkHeadline(rest, title) {
  return (
    parseField(rest, "Problem") ||
    parseField(rest, "Tonight") ||
    parseField(rest, "Case") ||
    rest.find((l) => /^#\d+/.test(l)) ||
    rest.find((l) => /^week\s+\d+/i.test(l)) ||
    // Prefer schedule connection / class over generic first line
    rest.find((l) => !/^(class today|day coding|pattern|schedule connection|why|do|apply|your product|for\s)/i.test(l)) ||
    rest[0] ||
    title
  );
}

function parseHomeworkTasks(content) {
  const raw = String(content || "").replace(/\r\n/g, "\n").trim();
  if (!raw) return [];

  const lines = raw
    .split("\n")
    .map((l) => l.replace(/^[▶•\-–—*▸▪\s]+/, "").trim())
    .filter((l) => l && !/^tonight'?s homework\b/i.test(l) && !/^tonight\s*\(/i.test(l));

  const starts = [];
  lines.forEach((line, i) => {
    if (TASK_TITLE_RE.test(line)) starts.push(i);
  });

  if (!starts.length) {
    const labeled = lines
      .filter((l) => /^(leetcode|exercism|linkedin|agent|case)\b/i.test(l))
      .slice(0, 5)
      .map((line) => {
        const url = (line.match(/https?:\/\/\S+/i) || [])[0] || null;
        const title = line.replace(url || "", "").replace(/\s*[—–-]\s*$/, "").trim();
        return {
          title,
          style: getTaskStyle(title),
          headline: title,
          bodyLines: [],
          doneWhen: "",
          url,
        };
      });
    if (labeled.length) return labeled;
    if (!lines.length) return [];
    const doneWhen = parseField(lines, "Deliverable") || parseField(lines, "Done when");
    return [
      {
        title: "Tonight's Homework",
        style: getTaskStyle("Task"),
        headline: pickHomeworkHeadline(lines, "Tonight's Homework"),
        bodyLines: lines.filter((l) => l !== pickHomeworkHeadline(lines, "Tonight's Homework")),
        doneWhen,
        url: null,
      },
    ];
  }

  return starts.slice(0, 5).map((start, idx) => {
    const end = idx + 1 < starts.length ? starts[idx + 1] : lines.length;
    const chunk = lines.slice(start, end);
    const title = chunk[0];
    const body = chunk.slice(1);

    let url = null;
    const rest = [];
    body.forEach((line) => {
      const m = line.match(/^(?:link\s*:\s*)?(https?:\/\/\S+)/i);
      if (m) {
        url = url || m[1].replace(/[.,;:!?)]+$/g, "");
        return;
      }
      rest.push(line);
    });

    const problem = pickHomeworkHeadline(rest, title);
    const doneWhen = parseField(rest, "Deliverable") || parseField(rest, "Done when");

    // Keep full "How it connects" blocks visible (learning + project)
    const collectHowBlock = (startRe) => {
      const idx = rest.findIndex((l) => startRe.test(l));
      if (idx < 0) return [];
      const lines = [rest[idx]];
      for (let i = idx + 1; i < rest.length; i++) {
        if (/^\d\)/.test(rest[i]) || /^[-·]/.test(rest[i])) lines.push(rest[i]);
        else break;
      }
      return lines;
    };
    const howLearn = collectHowBlock(/^how it connects to today's learning/i);
    const howProject = collectHowBlock(/^how it connects to project implementation/i);
    const howLines = [...howLearn, ...howProject];
    const scheduleConn =
      howLines.length > 0
        ? ""
        : parseField(rest, "Connects to") ||
          parseField(rest, "Schedule connection") ||
          "";

    // Repair incomplete LeetCode URLs from the problem name
    if (
      /leetcode/i.test(title) &&
      (!url ||
        /leetcode\.com\/(?:problems\/?\s*$|problemset)/i.test(url) ||
        /leetcode\.com\/problems\/?\s*$/i.test(url))
    ) {
      const slug = String(problem || "")
        .replace(/^#\d+\s*[—–-]?\s*/i, "")
        .replace(/\([^)]*\)\s*$/g, "")
        .replace(/\b(easy|medium|hard)\b/gi, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");
      if (slug && slug.length >= 2) url = `https://leetcode.com/problems/${slug}/`;
    }

    const stripHow = new Set(howLines.map((l) => l.toLowerCase()));
    const bodyLines = rest.filter((l) => {
      const low = l.toLowerCase();
      if (stripHow.has(low)) return false;
                  if (/^(problem|case|tonight|done when|deliverable|link|connects to|schedule connection|your project)\s*:/i.test(l))
        return false;
      if (l === problem) return false;
      if (/^#\d+/.test(l)) return false;
      return true;
    });

    const projectLine = parseField(rest, "Your project");
    if (projectLine) {
      bodyLines.unshift(`Your project: ${projectLine}`);
    }

    // Put connection explanation at the top of the homework card
    if (howLines.length) {
      bodyLines.unshift(...howLines);
    } else if (
      scheduleConn &&
      !bodyLines.some((l) => /connects to|schedule connection|how it connects/i.test(l))
    ) {
      bodyLines.unshift(`How it connects to today's learning: ${scheduleConn}`);
    } else {
      const classToday = parseField(rest, "Class today");
      const pattern = parseField(rest, "Pattern");
      const dayCp = parseField(rest, "Day Coding Practice");
      if (classToday || pattern) {
        bodyLines.unshift(
          `How it connects to today's learning:`,
          `1) Learning: you studied “${classToday || pattern}”.`,
          dayCp
            ? `2) Day slot: ${dayCp}${pattern ? ` (pattern “${pattern}”)` : ""}.`
            : `2) Day slot: Coding Practice on pattern “${pattern || "today's pattern"}”.`,
          `3) Tonight: this LeetCode drills that SAME part so practice continues class.`,
          `How it connects to project implementation:`,
          `1) Pattern “${pattern || "today's pattern"}” is a building block for your product.`,
          `2) Project hook: after solve, write where this pattern fits (feature / data / flow).`,
          `3) Implementation note: name the file or screen you would touch next.`
        );
      }
    }

    return {
      title,
      style: getTaskStyle(title),
      headline: problem,
      bodyLines,
      doneWhen,
      url,
    };
  });
}

function ActionBtn({ icon, label, onClick, disabled, primary = false, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title || label}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "4px 8px",
        borderRadius: 6,
        fontSize: 11,
        fontWeight: 700,
        border: `1px solid ${primary ? C.primary : C.border}`,
        background: primary ? C.primary : C.surface,
        color: primary ? "#fff" : disabled ? C.faint : C.text,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.55 : 1,
        whiteSpace: "nowrap",
      }}
    >
      {icon && <i className={`ti ${icon}`} style={{ fontSize: 14 }} />}
      {label}
    </button>
  );
}

export default function HomeworkPlanView({
  plan,
  dailyPlan = null,
  onOpenDaily,
  onPlanChange,
  onDayRegenerated,
  onSyncTeam,
  isTeam = false,
  emptyHint = "Generate a daily schedule first — homework is built from each day.",
  memberMeta = null,
}) {
  const entries = useMemo(() => {
    const filledDaily = homeworkRowsOnly(dailyPlan?.rows || []);
    if (homeworkHasBody(plan?.rows) && !dailyPlan?.rows?.length) {
      return extractHomeworkEntries(plan.rows);
    }
    if (filledDaily.length) return extractHomeworkEntries(filledDaily);
    if (homeworkHasBody(plan?.rows)) return extractHomeworkEntries(plan.rows);
    return extractHomeworkEntries(homeworkRowsOnly(plan?.rows || []));
  }, [plan?.rows, dailyPlan?.rows]);
  const weeks = useMemo(() => weekNumbersFromEntries(entries), [entries]);

  const [activeWeek, setActiveWeek] = useState(1);
  const [activeDay, setActiveDay] = useState("Monday");
  const [subTaskDone, setSubTaskDone] = useState(new Set());
  const [rowDone, setRowDone] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editText, setEditText] = useState("");
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [busy, setBusy] = useState(""); // sync | redo | refine | ""
  const [banner, setBanner] = useState(null);
  const [feedback, setFeedback] = useState("");
  const hasToken = typeof localStorage !== "undefined" && !!getAuthToken();
  const hwCols = useTablePersonalize("homework");

  useEffect(() => {
    if (weeks.length && !weeks.includes(activeWeek)) setActiveWeek(weeks[0]);
  }, [weeks, activeWeek]);

  const weekEntries = useMemo(
    () =>
      entries.filter(({ row }) => {
        const p = parseDayKey(row[0]);
        return p && (p.week || 1) === activeWeek;
      }),
    [entries, activeWeek]
  );

  const daysWithHw = useMemo(() => {
    const map = {};
    weekEntries.forEach(({ row }) => {
      const p = parseDayKey(row[0]);
      if (p?.dayName) map[p.dayName] = true;
    });
    return map;
  }, [weekEntries]);

  const dayEntry = useMemo(() => {
    const hit = weekEntries.find(({ row }) => {
      const p = parseDayKey(row[0]);
      return p && String(p.dayName).toLowerCase() === String(activeDay).toLowerCase();
    });
    return hit || weekEntries[0] || null;
  }, [weekEntries, activeDay]);

  const dayKey = dayEntry?.row?.[0] || null;
  const theme = DAY_THEMES[activeDay] || DAY_THEMES.Monday;
  const content = dayEntry?.row?.[3] || "";
  let hwMap = {};
  try {
    hwMap = splitStoredOverrides(JSON.parse(localStorage.getItem(SLOT_OVERRIDE_KEY) || "{}")).homework;
  } catch {
    hwMap = {};
  }
  const hwMapKey = JSON.stringify(hwMap);
  const tasks = useMemo(() => {
    const parsed = parseHomeworkTasks(content);
    const out = parsed
      .map((task) => {
        const fam = homeworkTaskFamily(task.style?.label, task.headline);
        const row = fam ? hwMap[fam] : null;
        if (!row) return task;
        if (row.mode === "hide") return { ...task, _hide: true };
        const title = row.title || task.style?.label || task.headline;
        return {
          ...task,
          headline: row.title || task.headline,
          bodyLines: row.notes ? [row.notes] : task.bodyLines,
          style: { ...task.style, label: title },
          _sort: row.sort,
        };
      })
      .filter((t) => !t._hide);
    Object.entries(hwMap).forEach(([id, row]) => {
      if (row?.mode !== "add") return;
      if (!row.title && !row.notes) return;
      out.push({
        headline: row.title || "New task",
        bodyLines: row.notes ? [row.notes] : [],
        style: { bg: "#FFFFFF", accent: "#374151", label: row.title || "Task" },
        url: "",
        doneWhen: "",
        added: true,
        _id: id,
        _sort: row.sort,
      });
    });
    out.sort((a, b) => (Number(a._sort) || 0) - (Number(b._sort) || 0));
    return out;
  }, [content, hwMapKey]);

  useEffect(() => {
    if (!dayEntry) {
      const p = weekEntries[0] && parseDayKey(weekEntries[0].row[0]);
      if (p?.dayName) setActiveDay(p.dayName);
    }
  }, [activeWeek, weekEntries, dayEntry]);

  // Progress from dedicated HomeworkNight collection
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!dayKey || dayEntry == null) {
        setSubTaskDone(new Set());
        setRowDone(false);
        return;
      }
      if (!hasToken) {
        setSubTaskDone(new Set());
        setRowDone(false);
        return;
      }
      try {
        const r = await getHomeworkPlan();
        if (cancelled) return;
        const night = (r.nights || []).find((n) => String(n.dayId) === String(dayKey));
        if (!night) {
          setSubTaskDone(new Set());
          setRowDone(false);
          return;
        }
        setRowDone(!!night.nightDone);
        const keys = new Set();
        (night.tasks || []).forEach((t, i) => {
          if (t.done) keys.add(String(t.index ?? i));
        });
        setSubTaskDone(keys);
      } catch {
        if (!cancelled) {
          setSubTaskDone(new Set());
          setRowDone(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [dayKey, dayEntry, hasToken, plan?.rows]);

  // Reset edit / undo when day or plan content changes from outside
  useEffect(() => {
    setEditMode(false);
    setEditText(content || "");
    setUndoStack([]);
    setRedoStack([]);
  }, [dayKey, content]);

  useEffect(() => {
    if (!banner) return undefined;
    const t = setTimeout(() => setBanner(null), 4500);
    return () => clearTimeout(t);
  }, [banner]);

  const doneCount = tasks.filter((_, i) => subTaskDone.has(String(i))).length;

  // Prefer full daily rows for "Redo this night" context; fall back to homework rows
  const dayRowsForKey = useMemo(() => {
    if (!dayKey) return [];
    const source = dailyPlan?.rows?.length ? dailyPlan.rows : plan?.rows || [];
    return source.filter((r) => Array.isArray(r) && String(r[0]) === String(dayKey));
  }, [dailyPlan?.rows, plan?.rows, dayKey]);

  const pushPlanChange = (nextPlan) => {
    if (!onPlanChange) return;
    if (plan) {
      setUndoStack((s) => [...s.slice(-39), JSON.parse(JSON.stringify(plan))]);
      setRedoStack([]);
    }
    onPlanChange(nextPlan);
  };

  const undoEdit = () => {
    if (!undoStack.length || !onPlanChange) return;
    const prev = undoStack[undoStack.length - 1];
    setUndoStack((s) => s.slice(0, -1));
    if (plan) setRedoStack((s) => [...s, JSON.parse(JSON.stringify(plan))]);
    onPlanChange(prev);
  };

  const redoEdit = () => {
    if (!redoStack.length || !onPlanChange) return;
    const next = redoStack[redoStack.length - 1];
    setRedoStack((s) => s.slice(0, -1));
    if (plan) setUndoStack((s) => [...s, JSON.parse(JSON.stringify(plan))]);
    onPlanChange(next);
  };

  const startEdit = () => {
    setEditText(content || "");
    setEditMode(true);
  };

  const cancelEdit = () => {
    setEditMode(false);
    setEditText(content || "");
  };

  const saveEdit = () => {
    if (!dayEntry || !plan?.rows || !onPlanChange) return;
    const targetDay = String(dayEntry.row?.[0] || dayEntry.dayId || "");
    const nextRows = plan.rows.map((r) => {
      if (!Array.isArray(r) || String(r[0]) !== targetDay) return r;
      if (!isHomeworkActivity(r[2]) && plan.module !== "homework") return r;
      return [r[0], r[1], r[2] || "Tonight's Homework", editText];
    });
    pushPlanChange({
      module: "homework",
      title: "Tonight's Homework",
      ...plan,
      rows: nextRows,
    });
    setEditMode(false);
    setBanner({ type: "ok", text: "Homework saved to Homework DB. Use Undo if you need to revert." });
  };

  const handleSync = async () => {
    setBusy("sync");
    try {
      if (isTeam && onSyncTeam) {
        await onSyncTeam();
        setBanner({ type: "ok", text: "Team homework synced and saved." });
      } else if (plan) {
        if (hasToken) {
          const res = await syncHomeworkPlan(plan, { source: "edit" });
          if (res?.plan && onPlanChange) onPlanChange(res.plan);
          else if (onPlanChange) onPlanChange({ ...plan });
        } else if (onPlanChange) {
          onPlanChange({ ...plan });
        }
        setBanner({ type: "ok", text: "Homework synced to the Homework collection." });
      } else {
        setBanner({ type: "warn", text: "No homework plan to sync yet." });
      }
    } catch (e) {
      const msg = e?.response?.data?.error || e.message || "Sync failed";
      setBanner({
        type: "err",
        text: /token|session|log in/i.test(msg)
          ? `${msg} — sign in again, then tap Sync.`
          : msg,
      });
    }
    setBusy("");
  };

  const handleRedoTonight = async () => {
    if (!dayKey || !dayRowsForKey.length) {
      setBanner({ type: "warn", text: "Open a homework day first." });
      return;
    }
    setBusy("redo");
    try {
      const learningRows = dayRowsForKey.filter((r) =>
        String(r[2] || "").toLowerCase().includes("learning")
      );
      const projectRow = dayRowsForKey.find((r) =>
        String(r[2] || "").toLowerCase().includes("project")
      );
      const userFb = String(feedback || "").trim();
      const context = {
        feedback: userFb
          ? `Redo Tonight's Homework only for this night. Faculty feedback: ${userFb}`
          : "Redo Tonight's Homework only: simpler beginner wording, clear A→B→C night path, keep Learning→SD→LC→Case→Project connectivity, Easy-leaning LC for early years, short Apply notes with Why. Complete Agent Workbench steps. Do not invent fake URLs.",
        subjects: [{ name: `${activeDay} subjects`, topic: "continue current week's topics" }],
        theme: {
          dsa: "",
          project: projectRow ? String(projectRow[3] || "").slice(0, 400) : "",
          systemDesign: "",
          tech: "",
        },
        learningHint: learningRows[0] ? String(learningRows[0][3] || "").slice(0, 240) : "",
      };
      const res = await regenerateDay(dayKey, context, dayRowsForKey, memberMeta || {});
      if (res.success && res.day?.rows) {
        onDayRegenerated?.(dayKey, res.day.rows);
        if (userFb) setFeedback("");
        setBanner({
          type: "ok",
          text: `Redid Week ${activeWeek} · ${activeDay} (including Tonight's Homework). Feedback saved as a lasting rule.`,
        });
      } else {
        setBanner({ type: "err", text: res.error || "Redo failed" });
      }
    } catch (e) {
      setBanner({
        type: "err",
        text: e?.response?.data?.error || e.message || "Day may be locked — unlock in Daily, then retry.",
      });
    }
    setBusy("");
  };

  const handleRefineHomework = async () => {
    const fb = String(feedback || "").trim();
    if (!fb) {
      setBanner({ type: "warn", text: "Add feedback first — then Refine." });
      return;
    }
    if (!plan?.rows?.length) {
      setBanner({ type: "warn", text: "No homework plan to refine yet." });
      return;
    }
    setBusy("refine");
    try {
      const hwPlan = {
        module: "homework",
        title: plan.title || "Tonight's Homework",
        columns: plan.columns || ["Day", "Time", "Activity", "Content"],
        rows: plan.rows,
      };
      const res = await refinePlan("homework", hwPlan, fb, {
        focusDayKey: dayKey || "",
      });
      if (res?.success && res.plan?.rows?.length) {
        pushPlanChange(res.plan);
        setFeedback("");
        setBanner({
          type: "ok",
          text: dayKey
            ? `Homework refined (focused on ${activeDay}). Feedback saved as a lasting rule.`
            : "Homework refined from your feedback. Feedback saved as a lasting rule.",
        });
      } else {
        setBanner({ type: "err", text: res?.error || "Refine failed" });
      }
    } catch (e) {
      setBanner({
        type: "err",
        text: e?.response?.data?.error || e.message || "Refine failed",
      });
    }
    setBusy("");
  };

  const handleResetProgress = async () => {
    if (!dayKey || dayEntry == null || !tasks.length) return;
    const prevSub = subTaskDone;
    const prevRow = rowDone;
    setSubTaskDone(new Set());
    setRowDone(false);
    try {
      if (hasToken) await resetHomeworkProgress(dayKey);
      setBanner({ type: "ok", text: "Progress cleared — redo the checks when you're ready." });
    } catch {
      setSubTaskDone(prevSub);
      setRowDone(prevRow);
      setBanner({ type: "err", text: "Could not reset progress." });
    }
  };

  const handleRedoTask = async (subIndex) => {
    if (!dayKey || dayEntry == null) return;
    const key = String(subIndex);
    if (!subTaskDone.has(key) && !rowDone) {
      setBanner({ type: "warn", text: "This task is already open to redo." });
      return;
    }
    const next = new Set(subTaskDone);
    next.delete(key);
    setSubTaskDone(next);
    try {
      if (hasToken) {
        await toggleHomeworkTask(dayKey, subIndex, false);
        if (rowDone) {
          setRowDone(false);
          await toggleHomeworkNight(dayKey, false);
        }
      }
      setBanner({ type: "ok", text: "Task reopened — mark done again when finished." });
    } catch {
      setSubTaskDone(subTaskDone);
    }
  };

  const downloadXLSX = () => {
    if (!entries.length) return;
    const wb = XLSX.utils.book_new();
    const weekDayMap = {};
    entries.forEach(({ row }) => {
      const parsed = parseDayKey(row[0]);
      if (!parsed) return;
      const sheetKey = `W${parsed.week}-${parsed.dayName}`;
      if (!weekDayMap[sheetKey]) {
        weekDayMap[sheetKey] = [["Day", "Type", "Task / Content"]];
      }
      const tasksForRow = parseHomeworkTasks(row[3]);
      if (!tasksForRow.length) {
        weekDayMap[sheetKey].push([row[0], "Homework", cellText(row[3])]);
        return;
      }
      tasksForRow.forEach((task) => {
        const content = [
          task.headline,
          ...(task.bodyLines || []),
          task.doneWhen ? `Deliverable: ${task.doneWhen}` : "",
          task.url ? `Link: ${task.url}` : "",
        ].filter(Boolean).join("\n");
        weekDayMap[sheetKey].push([
          `${parsed.dayName} (W${parsed.week})`,
          task.style?.label || task.title || "Task",
          content,
        ]);
      });
    });

    const sortedKeys = Object.keys(weekDayMap).sort((a, b) => {
      const [aw, ad] = a.split("-");
      const [bw, bd] = b.split("-");
      const wDiff = parseInt(aw.slice(1), 10) - parseInt(bw.slice(1), 10);
      if (wDiff !== 0) return wDiff;
      return DAY_NAMES.indexOf(ad) - DAY_NAMES.indexOf(bd);
    });

    sortedKeys.forEach((key) => {
      const rows = weekDayMap[key];
      const ws = XLSX.utils.aoa_to_sheet(rows);
      ws["!cols"] = [{ wch: 18 }, { wch: 22 }, { wch: 90 }];
      ws["!rows"] = rows.map((_, i) => ({ hpt: i === 0 ? 22 : 70 }));
      const range = XLSX.utils.decode_range(ws["!ref"] || "A1");
      for (let R = 1; R <= range.e.r; R++) {
        const addr = XLSX.utils.encode_cell({ r: R, c: 2 });
        if (ws[addr]) ws[addr].s = { alignment: { wrapText: true, vertical: "top" } };
      }
      XLSX.utils.book_append_sheet(wb, ws, key.slice(0, 31));
    });

    XLSX.writeFile(wb, `${sanitizeFilePart("tonight")}_homework_plan.xlsx`);
    setBanner({ type: "ok", text: "Excel downloaded (1 sheet per homework night)." });
  };

  const downloadCSV = () => {
    if (!entries.length) return;
    const esc = (val) => `"${cellText(val).replace(/"/g, '""')}"`;
    const lines = [["Week", "Day", "Type", "Task / Content"].map(esc).join(",")];
    entries.forEach(({ row }) => {
      const parsed = parseDayKey(row[0]) || { week: "", dayName: "" };
      const tasksForRow = parseHomeworkTasks(row[3]);
      if (!tasksForRow.length) {
        lines.push([parsed.week, parsed.dayName, "Homework", row[3]].map(esc).join(","));
        return;
      }
      tasksForRow.forEach((task) => {
        const content = [
          task.headline,
          ...(task.bodyLines || []),
          task.doneWhen ? `Deliverable: ${task.doneWhen}` : "",
          task.url ? `Link: ${task.url}` : "",
        ].filter(Boolean).join("\n");
        lines.push([
          parsed.week,
          parsed.dayName,
          task.style?.label || task.title || "Task",
          content,
        ].map(esc).join(","));
      });
    });
    const blob = new Blob(["\uFEFF" + lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${sanitizeFilePart("tonight")}_homework_plan.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setBanner({ type: "ok", text: "CSV downloaded." });
  };

  const switchWeek = (w) => {
    setActiveWeek(w);
    const hasDay = entries.some(({ row }) => {
      const p = parseDayKey(row[0]);
      return p && p.week === w && String(p.dayName).toLowerCase() === activeDay.toLowerCase();
    });
    if (!hasDay) setActiveDay("Monday");
  };

  const handleToggleSub = async (subIndex) => {
    if (!dayKey || dayEntry == null) return;
    const key = String(subIndex);
    const willBeDone = !subTaskDone.has(key);
    const next = new Set(subTaskDone);
    if (willBeDone) next.add(key); else next.delete(key);
    setSubTaskDone(next);
    try {
      if (hasToken) await toggleHomeworkTask(dayKey, subIndex, willBeDone);
      const allDone = tasks.every((_, i) =>
        i === subIndex ? willBeDone : next.has(String(i))
      );
      if (allDone !== rowDone) {
        setRowDone(allDone);
        if (hasToken) await toggleHomeworkNight(dayKey, allDone);
      }
    } catch {
      setSubTaskDone(subTaskDone);
    }
  };

  const handleToggleAll = async () => {
    if (!dayKey || dayEntry == null) return;
    const willBeDone = !rowDone;
    const prevSub = subTaskDone;
    const prevRow = rowDone;
    const next = new Set();
    if (willBeDone) tasks.forEach((_, i) => next.add(String(i)));
    setSubTaskDone(next);
    setRowDone(willBeDone);
    try {
      if (hasToken) {
        await toggleHomeworkNight(dayKey, willBeDone);
        await Promise.all(
          tasks.map((_, i) => toggleHomeworkTask(dayKey, i, willBeDone).catch(() => null))
        );
      }
    } catch {
      setSubTaskDone(prevSub);
      setRowDone(prevRow);
    }
  };

  const panelHeight = `calc(100dvh - ${LAYOUT.navbarHeight}px - 24px)`;

  if (!entries.length) {
    return (
      <div style={{ height: panelHeight, minHeight: 320, padding: 8 }}>
        <EmptyState
          icon={!hasToken ? "ti-lock" : "ti-moon-stars"}
          title={!hasToken ? "Sign in to unlock homework sync" : "No homework nights yet"}
          body={
            !hasToken
              ? "Log in so Tonight's Homework can save and sync. Then generate a daily plan — homework is built from each day."
              : (emptyHint || "Generate a daily schedule first — Tonight's Homework is built from each day.")
          }
          tone={!hasToken ? "warn" : "neutral"}
        />
      </div>
    );
  }

  return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      height: panelHeight,
      minHeight: 380,
      maxHeight: panelHeight,
      background: C.surface,
      border: `1px solid ${C.border}`,
      borderRadius: LAYOUT.radius,
      boxShadow: "0 1px 2px rgba(10,14,23,0.04), 0 8px 24px rgba(10,14,23,0.06)",
      overflow: "hidden",
    }}>
      <style>{`
        .hw-schedule-scroll {
          scrollbar-width: thin;
          scrollbar-color: #C5CDD8 transparent;
        }
        .hw-schedule-scroll::-webkit-scrollbar { width: 8px; height: 8px; }
        .hw-schedule-scroll::-webkit-scrollbar-thumb {
          background: #C5CDD8;
          border-radius: 8px;
        }
        .hw-schedule-scroll::-webkit-scrollbar-track { background: transparent; }
      `}</style>

      {/* Fixed top: week / day selectors — same pattern as Daily */}
      <div style={{
        flexShrink: 0,
        background: C.surfaceAlt || "#F7F9FC",
        padding: "12px 16px 10px",
        borderBottom: `1px solid ${C.border}`,
      }}>
        <div style={{ marginBottom: 10 }}>
          <p style={{
            fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase",
            letterSpacing: "0.05em", marginBottom: 4,
          }}>
            Week
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
            {weeks.map((w) => {
              const isActive = activeWeek === w;
              return (
                <button
                  key={w}
                  type="button"
                  onClick={() => switchWeek(w)}
                  style={{
                    padding: "3px 8px", borderRadius: 6, fontSize: 11,
                    fontWeight: isActive ? 700 : 500,
                    border: `1.5px solid ${isActive ? C.primary : C.border}`,
                    background: isActive ? C.primary : C.surface,
                    color: isActive ? "#fff" : C.muted,
                    cursor: "pointer",
                  }}
                >
                  <span style={{ fontWeight: 800 }}>W{w}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {DAY_NAMES.map((day) => {
            const isActive = activeDay === day;
            const dt = DAY_THEMES[day];
            const has = Boolean(daysWithHw[day]);
            return (
              <button
                key={day}
                type="button"
                onClick={() => has && setActiveDay(day)}
                disabled={!has}
                style={{
                  padding: "4px 9px", borderRadius: 6,
                  border: `1.5px solid ${isActive ? dt.color : C.border}`,
                  background: isActive ? dt.light : C.surface,
                  color: !has ? C.faint : isActive ? dt.color : C.muted,
                  fontWeight: isActive ? 700 : 500, fontSize: 11,
                  cursor: has ? "pointer" : "default",
                  opacity: has ? 1 : 0.45,
                  display: "flex", alignItems: "center", gap: 6,
                }}
              >
                {dt.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Gradient day bar — same as Daily */}
      <div style={{
        flexShrink: 0,
        background: C.primarySoft,
        padding: "6px 12px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        flexWrap: "wrap", gap: 6,
        borderBottom: `2px solid ${C.primary}`,
      }}>
        <span style={{ color: C.text, fontWeight: 800, fontSize: 13 }}>
          Week {activeWeek} · {theme.label}
          <span style={{ fontWeight: 500, fontSize: 12, marginLeft: 8, color: C.muted }}>
            Tonight&apos;s Homework
          </span>
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span style={{ color: C.muted, fontSize: 12, fontWeight: 600 }}>
            {doneCount}/{tasks.length} done
          </span>
          <span style={{ color: C.muted, fontSize: 12 }}>
            {tasks.length} task{tasks.length === 1 ? "" : "s"}
          </span>
          {tasks.length > 0 && (
            <label style={{
              display: "flex", alignItems: "center", gap: 6, cursor: "pointer",
              fontSize: 12, fontWeight: 700, color: C.text,
            }}>
              <input type="checkbox" checked={rowDone} onChange={handleToggleAll} />
              {rowDone ? "Clear all" : "Mark all done"}
            </label>
          )}
        </div>
      </div>

      {/* Table toolbar — Undo / Redo / Edit / Sync (features for the table) */}
      <div style={{
        flexShrink: 0,
        padding: "6px 10px",
        borderBottom: `1px solid ${C.border}`,
        background: editMode ? TABLE.editBanner : C.surfaceAlt || "#F7F9FC",
        display: "flex",
        flexWrap: "wrap",
        gap: 8,
        alignItems: "center",
      }}>
        <div style={{ fontSize: 11.5, color: C.text, flex: 1, minWidth: 160 }}>
          <strong>{editMode ? "Edit mode ON" : "Homework tools"}</strong>
          {" "}
          {editMode
            ? "Change the text below, then Save (writes to database)."
            : "Undo / Redo edits · Sync · Refine from feedback · Redo this night · Reset checks"}
        </div>
        <ActionBtn
          icon="ti-arrow-back-up"
          label="Undo"
          title="Undo last homework edit"
          onClick={undoEdit}
          disabled={!undoStack.length || !!busy}
        />
        <ActionBtn
          icon="ti-arrow-forward-up"
          label="Redo"
          title="Redo last undone edit"
          onClick={redoEdit}
          disabled={!redoStack.length || !!busy}
        />
        <TableColumnsMenu
          columns={HW_COLS}
          isOn={hwCols.isOn}
          toggle={hwCols.toggle}
          header={hwCols.header}
          rename={hwCols.rename}
          reset={hwCols.reset}
        />
        {onPlanChange && (
          editMode ? (
            <>
              <ActionBtn icon="ti-device-floppy" label="Save" onClick={saveEdit} primary disabled={!!busy} />
              <ActionBtn icon="ti-x" label="Cancel" onClick={cancelEdit} disabled={!!busy} />
            </>
          ) : (
            <ActionBtn
              icon="ti-pencil"
              label="Edit"
              title="Edit tonight's homework text"
              onClick={startEdit}
              disabled={!!busy || !dayEntry}
            />
          )
        )}
        <ActionBtn
          icon="ti-refresh"
          label={busy === "sync" ? "Syncing…" : isTeam ? "Sync team" : "Sync"}
          title={isTeam ? "Re-sync member homework from Daily" : "Sync homework to database"}
          onClick={handleSync}
          disabled={!!busy || !plan}
        />
        <ActionBtn
          icon="ti-rotate-clockwise"
          label={busy === "redo" ? "Redoing…" : "Redo this night"}
          title="Regenerate this day's schedule + Tonight's Homework"
          onClick={handleRedoTonight}
          disabled={!!busy || !dayKey || !dayRowsForKey.length}
        />
        <ActionBtn
          icon="ti-checkbox"
          label="Reset checks"
          title="Clear done checkmarks for tonight"
          onClick={handleResetProgress}
          disabled={!!busy || !tasks.length || (doneCount === 0 && !rowDone)}
        />
      </div>

      {banner && (
        <div style={{
          flexShrink: 0,
          padding: "8px 14px",
          fontSize: 12.5,
          fontWeight: 650,
          color: banner.type === "err" ? C.error : banner.type === "warn" ? "#9A7200" : "#047857",
          background: banner.type === "err" ? C.errorBg : banner.type === "warn" ? C.warningBg : C.successBg,
          borderBottom: `1px solid ${C.border}`,
        }}>
          {banner.text}
        </div>
      )}

      {editMode && (
        <div style={{ flexShrink: 0, padding: "10px 14px", borderBottom: `1px solid ${C.border}`, background: "#fff" }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.muted, marginBottom: 6 }}>
            Edit Tonight&apos;s Homework — Week {activeWeek} · {activeDay}
          </div>
          <textarea
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            rows={10}
            style={{
              width: "100%",
              boxSizing: "border-box",
              fontSize: 13,
              lineHeight: 1.5,
              padding: "10px 12px",
              borderRadius: 8,
              border: `1px solid ${C.border}`,
              fontFamily: "inherit",
              resize: "vertical",
              color: C.text,
            }}
          />
        </div>
      )}

      {/* Scrollable table */}
      <div
        className="hw-schedule-scroll"
        style={{
          flex: 1,
          minHeight: 0,
          overflowX: "auto",
          overflowY: "auto",
          background: "#fff",
        }}
      >
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 640 }}>
          <colgroup>
            {HW_COLS.filter((c) => hwCols.isOn(c.id)).map((c) => (
              <col key={c.id} style={{ width: c.width }} />
            ))}
          </colgroup>
          <thead>
            <tr style={{ background: TABLE.homework.headBg }}>
              {HW_COLS.filter((c) => hwCols.isOn(c.id)).map((col, i, arr) => (
                <th
                  key={col.id}
                  style={{
                    padding: "5px 10px",
                    textAlign: "left",
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: 10,
                    borderRight: i < arr.length - 1 ? `1px solid ${TABLE.homework.headBorder}` : "none",
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    background: TABLE.homework.headBg,
                    position: "sticky",
                    top: 0,
                    zIndex: 2,
                    boxShadow: `0 1px 0 ${TABLE.homework.accent}`,
                  }}
                >
                  {hwCols.header(col.id, col.label)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!dayEntry || tasks.length === 0 ? (
              <tr>
                <td colSpan={Math.max(1, HW_COLS.filter((c) => hwCols.isOn(c.id)).length)} style={{ padding: "40px", textAlign: "center", color: C.muted }}>
                  No homework for Week {activeWeek} – {activeDay}. Regenerate the plan if this looks wrong.
                </td>
              </tr>
            ) : (
              tasks.map((task, i) => {
                const done = subTaskDone.has(String(i));
                const rs = task.style;
                const contentText = [
                  task.headline,
                  ...task.bodyLines,
                  task.doneWhen ? `Deliverable: ${task.doneWhen}` : "",
                  task.url ? `Link: ${task.url}` : "",
                ].filter(Boolean).join("\n");

                return (
                  <tr
                    key={i}
                    style={{
                      borderBottom: `1px solid ${C.border}`,
                      background: done ? "#F0FDF4" : rs.bg,
                      opacity: done ? 0.7 : 1,
                    }}
                  >
                    {hwCols.isOn("check") && (
                    <td style={{
                      padding: "6px 6px", textAlign: "center",
                      borderRight: `1px solid ${C.border}`, verticalAlign: "top",
                    }}>
                      <input
                        type="checkbox"
                        checked={done}
                        onChange={() => handleToggleSub(i)}
                        style={{ width: 16, height: 16, cursor: "pointer", marginTop: 4 }}
                      />
                    </td>
                    )}
                    {hwCols.isOn("type") && (
                    <td style={{
                      padding: "6px 10px",
                      color: rs.accent,
                      fontWeight: 700,
                      fontSize: 11.5,
                      borderRight: `1px solid ${C.border}`,
                      verticalAlign: "top",
                      textDecoration: done ? "line-through" : "none",
                    }}>
                      {rs.label}
                    </td>
                    )}
                    {hwCols.isOn("content") && (
                    <td style={{
                      padding: "6px 10px",
                      verticalAlign: "top",
                      textDecoration: done ? "line-through" : "none",
                    }}>
                      <div style={{ fontWeight: 700, fontSize: 12.5, color: C.text, marginBottom: 2 }}>
                        {task.headline}
                      </div>
                      {task.bodyLines.length > 0 && (
                        <div style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.55 }}>
                          <RichLineText text={task.bodyLines.join("\n")} />
                        </div>
                      )}
                      {task.doneWhen && (
                        <div style={{ marginTop: 6, fontSize: 12.5, color: C.text }}>
                          <span style={{ fontWeight: 700, color: C.muted }}>Deliverable · </span>
                          {task.doneWhen}
                        </div>
                      )}
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8, alignItems: "center" }}>
                        {task.url && (
                          <a
                            href={task.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              fontSize: 12,
                              fontWeight: 700,
                              color: rs.accent,
                            }}
                          >
                            Open link →
                          </a>
                        )}
                        {done && (
                          <button
                            type="button"
                            onClick={() => handleRedoTask(i)}
                            style={{
                              fontSize: 11.5,
                              fontWeight: 700,
                              color: C.primary,
                              background: C.primarySoft,
                              border: `1px solid ${C.primary}44`,
                              borderRadius: 6,
                              padding: "4px 10px",
                              cursor: "pointer",
                            }}
                          >
                            Redo this
                          </button>
                        )}
                      </div>
                      <span style={{ display: "none" }}>{contentText}</span>
                    </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Refine from feedback — same pattern as Daily / Weekly */}
      <div style={{
        flexShrink: 0,
        borderTop: `1px solid ${C.border}`,
        padding: "14px 14px 12px",
        background: C.surface,
      }}>
        <p style={{ fontSize: 12, fontWeight: 700, color: C.muted, marginBottom: 4 }}>
          <i className="ti ti-sparkles" style={{ fontSize: 13, marginRight: 4, color: C.primary }} />
          Additional instructions (refine homework)
        </p>
        <p style={{ fontSize: 11, color: C.muted, marginBottom: 8, lineHeight: 1.4 }}>
          Updates tonight&apos;s homework from your feedback (focuses on the night you&apos;re viewing).
          Feedback is also saved as a lasting rule so new plans won&apos;t repeat the same mistake.
        </p>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <input
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && handleRefineHomework()}
            disabled={!plan?.rows?.length || !!busy}
            placeholder='e.g. "Make LeetCode shorter with Why · finish Agent Workbench steps · simpler case"'
            style={{
              flex: 1,
              minWidth: 220,
              padding: "9px 14px",
              fontSize: 13,
              border: `1.5px solid ${C.border}`,
              borderRadius: 8,
              outline: "none",
              color: C.text,
              background: plan?.rows?.length ? C.surface : C.bg,
            }}
          />
          <ActionBtn
            icon="ti-sparkles"
            label={busy === "refine" ? "Refining…" : "Refine →"}
            title="Refine homework from feedback (like Daily / Weekly)"
            onClick={handleRefineHomework}
            disabled={!plan?.rows?.length || !!busy || !feedback.trim()}
            primary
          />
          <ActionBtn
            icon="ti-rotate-clockwise"
            label={busy === "redo" ? "Redoing…" : "Redo night"}
            title="Regenerate this night's homework (uses feedback box if filled)"
            onClick={handleRedoTonight}
            disabled={!!busy || !dayKey || !dayRowsForKey.length}
          />
        </div>
      </div>

      {/* Downloads BELOW the table — same as Daily / Weekly */}
      <div style={{
        flexShrink: 0,
        borderTop: `1px solid ${C.border}`,
        padding: "12px 14px",
        background: C.surfaceAlt || "#F7F9FC",
        display: "flex",
        flexWrap: "wrap",
        gap: 8,
        alignItems: "center",
      }}>
        <ActionBtn
          icon="ti-file-spreadsheet"
          label="Excel (1 sheet/night)"
          title="Download Tonight's Homework as Excel"
          onClick={downloadXLSX}
          disabled={!entries.length}
          primary
        />
        <ActionBtn
          icon="ti-download"
          label="CSV"
          title="Download Tonight's Homework as CSV"
          onClick={downloadCSV}
          disabled={!entries.length}
        />
        {onOpenDaily && (
          <ActionBtn
            icon="ti-calendar"
            label="Open Daily"
            title="Jump to this day in the Daily schedule"
            onClick={() => onOpenDaily({ week: activeWeek, day: activeDay })}
            disabled={!!busy}
          />
        )}
      </div>
    </div>
  );
}
